/**
 * The composer (boards C1–C4): the attachment button and its popover, the tray of files waiting to
 * go, the field, and the microphone that becomes the send button once there is something to send.
 * Recording replaces the row with the recorder, and a finished take with its review.
 *
 * Enter sends and Shift+Enter breaks the line — never while an input method is composing a word.
 * A file pasted into the field or dropped on the conversation joins the tray rather than leaving at
 * once (the canon: „Najpierw zobaczysz podgląd"); the text in the field goes as the LAST file's
 * caption, which is the web's addition to what the phone sends.
 */

import {
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';

import { barsOf, durationLabel } from '../model/voicePeaks';
import type { CardChoice, SendIntent } from '../model/chatSession';
import { useVoiceRecorder } from '../lib/useVoiceRecorder';

import { AttachPopover, type PickerKind } from './AttachPopover';
import { ClipGlyph, CloseGlyph, MicGlyph, PlayGlyph, SendGlyph, StopGlyph, TrashGlyph } from './ChatGlyphs';
import styles from './chat.module.css';

export interface TrayItem {
  readonly id: string;
  readonly file: File;
  readonly kind: 'image' | 'video';
  readonly url: string;
}

/** At most this many files wait in the tray at once — the canon's drop handler takes four. */
export const TRAY_LIMIT = 4;

export function trayItemsOf(files: readonly File[]): TrayItem[] {
  return files
    .filter((file) => file.type.startsWith('image/') || file.type.startsWith('video/'))
    .map((file) => ({
      id: crypto.randomUUID(),
      file,
      kind: file.type.startsWith('video/') ? ('video' as const) : ('image' as const),
      url: URL.createObjectURL(file),
    }));
}

function sizeLabel(bytes: number, locale: string): string {
  const format = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  return bytes >= 1_048_576 ? `${format.format(bytes / 1_048_576)} MB` : `${format.format(Math.round(bytes / 1024))} KB`;
}

const MAX_TEXT = 4_000;

interface ComposerProps {
  readonly customerId: number | null;
  readonly draft: string;
  readonly onDraft: (text: string) => void;
  readonly tray: readonly TrayItem[];
  readonly onTray: (items: readonly TrayItem[]) => void;
  readonly attachOpen: boolean;
  readonly picker: PickerKind | null;
  readonly onAttach: (open: boolean, picker: PickerKind | null) => void;
  /** Sends one intent — a raw file or a raw recording; preparing it is the session's. */
  readonly send: (intent: SendIntent) => void;
  /** The trainer typed (the session throttles the signal). */
  readonly onTyped: () => void;
  /** Something was sent: the thread scrolls to it and the new-messages marker leaves. */
  readonly onSent: () => void;
  /** A sentence for the pane's live region — a refusal the trainer has to hear. */
  readonly onAnnounce: (message: string) => void;
  readonly fieldRef: RefObject<HTMLTextAreaElement | null>;
}

export function Composer({
  customerId,
  draft,
  onDraft,
  tray,
  onTray,
  attachOpen,
  picker,
  onAttach,
  send: sendIntent,
  onTyped,
  onSent,
  onAnnounce,
  fieldRef,
}: ComposerProps) {
  const { t, i18n } = useTranslation();
  const recorder = useVoiceRecorder();
  const fileInput = useRef<HTMLInputElement>(null);
  const [accept, setAccept] = useState<'image' | 'video'>('image');

  const canSend = draft.trim() !== '' || tray.length > 0;

  const addFiles = (files: readonly File[]) => {
    const room = TRAY_LIMIT - tray.length;
    if (room <= 0) {
      onAnnounce(t('web:chat.tray.tooMany', { count: TRAY_LIMIT }));
      return;
    }
    const items = trayItemsOf(files);
    if (items.length > room) onAnnounce(t('web:chat.tray.tooMany', { count: TRAY_LIMIT }));
    for (const extra of items.slice(room)) URL.revokeObjectURL(extra.url);
    if (items.length > 0) onTray([...tray, ...items.slice(0, room)]);
  };

  const send = () => {
    if (!canSend) return;
    const text = draft.trim().slice(0, MAX_TEXT);
    if (tray.length === 0) {
      sendIntent({ kind: 'text', text });
    } else {
      // The text goes as the LAST file's caption; the tray's own previews are released here, because
      // the session makes its own for the bubbles.
      tray.forEach((item, index) => {
        const caption = index === tray.length - 1 ? text : '';
        sendIntent({ kind: item.kind, file: item.file, caption });
        URL.revokeObjectURL(item.url);
      });
    }
    onDraft('');
    onTray([]);
    onAttach(false, null);
    onSent();
  };

  const sendCard = (choice: CardChoice) => {
    sendIntent({ kind: 'card', choice });
    onAttach(false, null);
    onSent();
  };

  const sendVoice = () => {
    if (recorder.state.phase !== 'review') return;
    const take = recorder.state;
    sendIntent({ kind: 'voice', recording: take.recording, durationSeconds: take.seconds, levels: take.levels });
    recorder.discard();
    onSent();
  };

  const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onDraft(event.target.value);
    if (event.target.value.trim() !== '') onTyped();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send();
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData.files);
    if (files.length === 0) return;
    event.preventDefault();
    addFiles(files);
  };

  const pickFiles = (kind: 'image' | 'video') => {
    setAccept(kind);
    onAttach(false, null);
    // The input's `accept` has to be set before the dialog opens.
    requestAnimationFrame(() => {
      fileInput.current?.click();
    });
  };

  const rows = Math.min(8, Math.max(1, draft.split('\n').length + Math.floor(draft.length / 70)));
  const state = recorder.state;

  return (
    <div className={styles.composer} role="region" aria-label={t('web:chat.composer.label')}>
      {attachOpen ? (
        <AttachPopover
          customerId={customerId}
          picker={picker}
          onPicker={(next) => {
            onAttach(true, next);
          }}
          onPickFiles={pickFiles}
          onSendCard={sendCard}
          onClose={() => {
            onAttach(false, null);
            fieldRef.current?.focus();
          }}
        />
      ) : null}
      <input
        ref={fileInput}
        className={styles.sr}
        type="file"
        tabIndex={-1}
        aria-hidden="true"
        accept={accept === 'image' ? 'image/*' : 'video/*'}
        multiple={accept === 'image'}
        onChange={(event) => {
          addFiles(Array.from(event.target.files ?? []));
          event.target.value = '';
          fieldRef.current?.focus();
        }}
      />
      {tray.length > 0 ? (
        <div className={styles.tray} aria-label={t('web:chat.tray.label')} role="group">
          {tray.map((item) => (
            <div className={styles.trayItem} key={item.id}>
              <span className={styles.trayThumb}>
                {item.kind === 'image' ? (
                  <img src={item.url} alt="" />
                ) : (
                  <>
                    <video src={item.url} muted playsInline preload="metadata" />
                    <span className={styles.playMini} aria-hidden="true">
                      <PlayGlyph size={11} />
                    </span>
                  </>
                )}
              </span>
              <span className={styles.trayText}>
                <span className={styles.trayName}>{item.file.name}</span>
                <span className={`${styles.traySize} ${styles.num}`}>{sizeLabel(item.file.size, i18n.language)}</span>
              </span>
              <button
                className={styles.trayRemove}
                type="button"
                aria-label={t('web:chat.tray.remove')}
                onClick={() => {
                  URL.revokeObjectURL(item.url);
                  onTray(tray.filter((other) => other.id !== item.id));
                }}
              >
                <CloseGlyph size={14} />
              </button>
            </div>
          ))}
          <span className={styles.trayHint}>{t('web:chat.tray.hint')}</span>
        </div>
      ) : null}

      {state.phase === 'recording' ? (
        <div className={styles.rec} role="group" aria-label={t('web:chat.voice.recording')}>
          <button className={styles.recCancel} type="button" onClick={recorder.cancel} aria-label={t('chat.voice.cancel')}>
            <TrashGlyph />
          </button>
          <span className={styles.recDot} aria-hidden="true" />
          <span className={`${styles.recClock} ${styles.num}`}>{durationLabel(state.seconds)}</span>
          <span className={styles.recBars} aria-hidden="true">
            {state.levels.slice(-46).map((level, index) => (
              // A rolling window: the position IS the identity of a bar.
              <i key={index} style={{ height: `${String(4 + level * 0.9)}px` }} />
            ))}
          </span>
          <button className={styles.recStop} type="button" onClick={recorder.stop} aria-label={t('chat.voice.stop')}>
            <StopGlyph />
          </button>
        </div>
      ) : null}

      {state.phase === 'review' ? (
        <div className={`${styles.rec} ${styles.recDone}`} role="group" aria-label={t('web:chat.voice.review')}>
          <button className={styles.recCancel} type="button" onClick={recorder.discard} aria-label={t('chat.voice.discard')}>
            <TrashGlyph />
          </button>
          <span className={`${styles.recClock} ${styles.num}`}>{durationLabel(state.seconds)}</span>
          <span className={`${styles.recBars} ${styles.recBarsStill}`} aria-hidden="true">
            {barsOf(state.levels, 40).map((level, index) => (
              <i key={index} style={{ height: `${String(4 + level * 0.8)}px` }} />
            ))}
          </span>
          <button
            className={`${styles.send} ${styles.sendOn}`}
            type="button"
            onClick={sendVoice}
            aria-label={t('chat.voice.send')}
          >
            <SendGlyph />
          </button>
        </div>
      ) : null}

      {state.phase === 'idle' && recorder.refusal !== null ? (
        <div className={styles.micRefusal} role="alert">
          <MicGlyph size={16} />
          <p className={styles.micRefusalText}>
            <b>{t(`web:chat.voice.micRefusal.${recorder.refusal}.title`)}</b>
            <span>{t(`web:chat.voice.micRefusal.${recorder.refusal}.fix`)}</span>
          </p>
          <button
            className={styles.micRefusalClose}
            type="button"
            aria-label={t('web:chat.voice.micRefusal.close')}
            onClick={recorder.dismissRefusal}
          >
            <CloseGlyph size={14} />
          </button>
        </div>
      ) : null}

      {state.phase === 'idle' ? (
        <div className={styles.composerRow}>
          <button
            className={`${styles.iconButton} ${attachOpen ? styles.iconButtonOn : ''}`}
            type="button"
            aria-label={t('chat.composer.attach')}
            aria-expanded={attachOpen}
            onClick={() => {
              onAttach(!attachOpen, null);
            }}
          >
            <ClipGlyph />
          </button>
          <label className={styles.field}>
            <span className={styles.sr}>{t('web:chat.composer.field')}</span>
            <textarea
              ref={fieldRef}
              rows={rows}
              value={draft}
              maxLength={MAX_TEXT}
              onChange={onChange}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              placeholder={t('chat.composer.placeholder')}
            />
          </label>
          {canSend ? (
            <button className={`${styles.send} ${styles.sendOn}`} type="button" onClick={send} aria-label={t('chat.composer.send')}>
              <SendGlyph />
            </button>
          ) : (
            <button className={`${styles.send} ${styles.mic}`} type="button" onClick={recorder.start} aria-label={t('chat.composer.recordVoice')}>
              <MicGlyph size={19} />
            </button>
          )}
        </div>
      ) : null}
      <div className={styles.composerHint}>
        <span>{t('web:chat.composer.hint')}</span>
      </div>
    </div>
  );
}
